import React, { ReactNode, useEffect } from 'react';
import { useSecureConfigStore, useConfigStore } from './store/config/useConfigStore';
import { LoadingOverlay } from 'components/core/loading-overlay';
import { TriangleAlert } from 'lucide-react';
import { useSiteConfigurationSettings } from 'hooks/use-configuration-settings';
import { HttpError } from 'lib/https';
import { useAuthStore } from './store/auth/useAuthStore';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionStore } from './store/subscription';
import { initializeCurrentPlan } from 'features/subscription/services/subscription-service';
import { useGetAccount } from 'features/profile/hooks/use-account';
import { useCommonStore } from './store/common/useCommonStore';
import { trackEvent } from 'analytics/tracker';
import { EVENTS } from 'analytics/events';
import posthog from 'analytics/posthogClient';
import { useCollaborationSocketStore } from './store/collaboration/use-collaboration-store';

export const PostAuthMiddleware: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { data: siteConfigurationsSettings, isLoading, error } = useSiteConfigurationSettings();
  const { setSiteConfigurationSettings } = useSecureConfigStore();
  const { configurationSettings } = useConfigStore();
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const { setCurrentPlan, setLoading, setError } = useSubscriptionStore();

  // Fetch user account data and update common store
  const { data: accountData, isLoading: accountLoading } = useGetAccount({
    enabled: !!configurationSettings
  });
  const { setUser } = useCommonStore();

  useEffect(() => {
    if (siteConfigurationsSettings) {
      setSiteConfigurationSettings(siteConfigurationsSettings);
    }
  }, [siteConfigurationsSettings, setSiteConfigurationSettings]);

  useEffect(() => {
    if (configurationSettings) {
      // Initialize subscription state after configuration is loaded
      initializeCurrentPlan(configurationSettings, setCurrentPlan, setLoading, setError);
    }
  }, [configurationSettings, setCurrentPlan, setError, setLoading]);

  // Update common store with fresh user data when account data is available
  useEffect(() => {
    if (accountData && !accountLoading) {
      setUser(accountData);
      trackEvent(EVENTS.USER_LOGGED_IN(accountData.itemId));
      try {
        posthog.identify(accountData.itemId);
      } catch (error) {
        console.log('Failed to identify user:', error);
      }
    }
  }, [accountData, accountLoading, setUser]);

  // Initialize collaboration websocket after login and configuration is ready
  const init = useCollaborationSocketStore((state) => state.init);
  const connectionStatus = useCollaborationSocketStore((state) => state.connectionStatus);
  const storedConfig = useCollaborationSocketStore((state) => state.configurationSettings);

  // Use ref to track if we've already initialized to prevent re-initialization on navigation
  const hasInitializedRef = React.useRef(false);

  useEffect(() => {

    if (
      configurationSettings &&
      accountData &&
      !accountLoading &&
      !hasInitializedRef.current &&
      (connectionStatus === 'DISCONNECTED' || storedConfig?.blocksKey !== configurationSettings.blocksKey)
    ) {

      init(configurationSettings);
      hasInitializedRef.current = true;
    }

    if (!accountData && hasInitializedRef.current) {
      hasInitializedRef.current = false;
    }

  }, [
    configurationSettings?.blocksKey,
    accountData?.itemId,
    accountLoading,
    connectionStatus,
    storedConfig?.blocksKey
  ]);

  if (isLoading || accountLoading) {
    return <LoadingOverlay />;
  }

  if (error || !siteConfigurationsSettings) {
    const errorMessage = error?.message ?? 'Failed to load configuration settings';
    const parseError = error instanceof HttpError ? error.error : { error: errorMessage };
    if (parseError.error === 'invalid_refresh_token') {
      logout();
      navigate('/login');
    }
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
        <div className="rounded-lg bg-red-50 border border-red-400 p-4 mb-2 transition-all duration-1000 ease-in-out opacity-100">
          <div className="flex items-center gap-1">
            <TriangleAlert className="text-error w-4 h-4" />
            <h1 className="text-error text-sm font-semibold">Something went wrong</h1>
          </div>
          <p className="text-error text-xs font-normal">{errorMessage}</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};


